import React, {
  useEffect,
  Suspense,
  useState,
  useCallback,
  useRef,
} from "react";
import "./App.css";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaArrowUp } from "react-icons/fa";
import SEO from "./components/SEO";
import Loader from "./components/Loader";

// Lazy load components for better performance
const Navbar = React.lazy(() => import("./Client/NavbarPage/Navbar"));
const Sidebar = React.lazy(() => import("./Client/SideBarPage/Sidebar"));
const Home = React.lazy(() => import("./Client/HomePage/Home"));
const About = React.lazy(() => import("./Client/AboutPage/About"));
const Experience = React.lazy(() => import("./Client/ExperiencePage/Experience"));
const EngineeringCaseStudies = React.lazy(() =>
  import("./Client/EngineeringCaseStudiesPage/EngineeringCaseStudies")
);
const Projects = React.lazy(() => import("./Client/ProjectsPage/Projects"));
const Certifications = React.lazy(() =>
  import("./Client/CertificationsPage/Certifications")
);
const Articles = React.lazy(() => import("./Client/ArticlesPage/Articles"));
const Resume = React.lazy(() => import("./Client/ResumePage/Resume"));
const Contact = React.lazy(() => import("./Client/ContactPage/Contact"));
const Footer = React.lazy(() => import("./Client/FooterPage/Footer"));

// Loading fallback component with hamster wheel animation
const LoadingFallback = () => <Loader />;

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const rafRef = useRef(0);

  // Coalesce scroll work into a single rAF frame to keep the main thread free
  // and avoid a setState per scroll event (smooth 60fps progress bar).
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollableHeight = documentHeight - windowHeight;
      const progress =
        scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
      setShowBackToTop(scrollTop > 300);
    });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    // Calm, premium scroll reveals — consistent ease-out, 300–500ms range.
    AOS.init({
      duration: 500,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
      delay: 0,
      disable: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    });
    AOS.refresh();

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  // Cursor-responsive glow: delegate pointer movement to any `.motion-glow`
  // surface and expose --mx / --my for the radial highlight. Skipped when the
  // user prefers reduced motion or on coarse (touch) pointers.
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (reduce || !fine) return;

    const handlePointerMove = (event) => {
      const target = event.target.closest?.(".motion-glow");
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      target.style.setProperty("--mx", `${x}%`);
      target.style.setProperty("--my", `${y}%`);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, []);

  return (
    <div className="App">
      <SEO />
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      {/* Scroll Progress Indicator */}
      <div
        className="scroll-progress-container"
        role="progressbar"
        aria-valuenow={scrollProgress}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Page scroll progress"
      >
        <div
          className="scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      <Suspense fallback={<LoadingFallback />}>
        <Navbar />
        <Sidebar />
        <main id="main" tabIndex={-1}>
          <Home />
          <About />
          <Experience />
          <EngineeringCaseStudies />
          <Projects />
          <Articles />
          <Resume />
          <Certifications />
          <Contact />
        </main>
        <Footer />
      </Suspense>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Back to top"
        >
          <FaArrowUp aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default React.memo(App);
